/**
 * @spec docs/specs/features/staking-position-ui.md#wallet-integration
 * Modal showing full wallet addresses with copy, explorer links, and wallet switching
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type { ConnectedWallet } from '@privy-io/react-auth';
import { cn } from '@/lib/cn';
import { getStakingChainId, getAddressUrl } from '@/lib/chains';
import { useAuth } from '@/hooks/useAuth';
import { showToast, TOAST_MESSAGES } from '@/lib/toast';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { WalletAddress } from '../ui/WalletAddress';
import CloseIcon from '@/assets/icons/close.svg?react';
import CheckIcon from '@/assets/icons/check.svg?react';
import LinkExternalIcon from '@/assets/icons/link-external.svg?react';

export interface WalletDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  wallets: ConnectedWallet[];
  selectedAddress: string;
  onSelectWallet: (address: string) => void;
  getWalletTypeLabel: (clientType: string) => string;
}

export function WalletDetailModal({
  isOpen,
  onClose,
  wallets,
  selectedAddress,
  onSelectWallet,
  getWalletTypeLabel,
}: WalletDetailModalProps): JSX.Element | null {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      showToast.success(TOAST_MESSAGES.LOGGED_OUT);
      onClose();
      navigate('/');
    } catch {
      showToast.error(TOAST_MESSAGES.LOGOUT_FAILED);
    } finally {
      setIsLoggingOut(false);
    }
  }, [logout, onClose, navigate]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const chainId = getStakingChainId();

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby="wallet-detail-title">
        <Card className="w-full max-w-md" padding="lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle id="wallet-detail-title">Wallet Details</CardTitle>
              <button
                onClick={onClose}
                className="text-text-muted hover:text-text-primary transition-colors"
                aria-label="Close"
                type="button"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
          </CardHeader>

          <CardContent>
            <div className="space-y-2">
              {wallets.map((wallet) => {
                const isSelected = wallet.address.toLowerCase() === selectedAddress.toLowerCase();
                const explorerUrl = getAddressUrl(chainId, wallet.address);

                return (
                  <div
                    key={wallet.address}
                    className={cn(
                      'flex items-center gap-3 p-3 rounded-lg transition-colors',
                      isSelected
                        ? 'bg-accent-purple/10 border border-accent-purple/30'
                        : 'bg-bg-secondary border border-border'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        onSelectWallet(wallet.address);
                        onClose();
                      }}
                      className="flex-1 min-w-0 text-left"
                    >
                      <p className="text-sm text-text-secondary mb-1">
                        {getWalletTypeLabel(wallet.walletClientType)}
                        {isSelected && (
                          <CheckIcon className="inline-block w-4 h-4 text-accent-purple ml-2" />
                        )}
                      </p>
                      <WalletAddress address={wallet.address} full showCopy />
                    </button>

                    <a
                      href={explorerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 p-2 text-text-muted hover:text-text-primary transition-colors"
                      aria-label="View on explorer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <LinkExternalIcon className="w-4 h-4" />
                    </a>
                  </div>
                );
              })}
            </div>

            {/* Log Out */}
            <div className="mt-4 pt-4 border-t border-border">
              <button
                type="button"
                onClick={() => void handleLogout()}
                disabled={isLoggingOut}
                className={cn(
                  'w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  'text-red-400 hover:bg-red-400/10',
                  isLoggingOut && 'opacity-50 cursor-not-allowed'
                )}
              >
                {isLoggingOut ? 'Logging out...' : 'Log Out'}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
